import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import type { WheelEvent } from 'react';
import { Control, Controller } from 'react-hook-form';
import MuiReactSelect, {
  useMuiSelectStyles,
} from 'src/components/muiReactSelect';
import countries from 'src/constants/CountryData';
import { CheckoutLabel } from 'src/pages/Store/styles/Checkout.styles';
import { CheckoutDetails } from 'src/redux/types/store';

// Two columns where the caller has the width for it: stacked, the seven fields
// are taller than the sheet they open in, and the alternative to laying them
// side by side is a scrollbar.
const FormFields = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'twoColumn',
})<{ twoColumn?: boolean }>(({ theme, twoColumn }) => ({
  display: 'grid',
  gap: '12px 14px',
  gridTemplateColumns: twoColumn ? '1fr 1fr' : '1fr',
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const FullRow = styled(Box)({ gridColumn: '1 / -1' });

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10}$/;
const PINCODE_REGEX = /^[0-9]{6}$/;

// Mirrors the Controller `rules` below exactly. Callers use this to check a
// fetched saved address BEFORE this form (and its Controllers) ever mount —
// react-hook-form's own trigger()/isValid can't be trusted for that because
// unregistered fields always validate as true.
export const isBillingAddressValid = (data: CheckoutDetails): boolean =>
  data.name.trim().length >= 5 &&
  EMAIL_REGEX.test(data.email) &&
  PHONE_REGEX.test(data.phone.number) &&
  data.billingAddress.address.trim().length >= 5 &&
  PINCODE_REGEX.test(data.billingAddress.pincode) &&
  data.billingAddress.state.trim().length >= 3 &&
  !!data.billingAddress.country;

// Credits/top-up purchases only need a billing address — no shipping fields,
// no "billing same as shipping" toggle. Only the fields registered here (via
// Controller) count toward the form's isValid, so keeping shippingAddress
// unregistered is what lets the caller validate billing-only.
const BillingAddressForm = ({
  control,
  onClose,
  onSubmit,
  twoColumn,
}: {
  control: Control<CheckoutDetails>;
  onClose: () => void;
  onSubmit: () => void;
  /** Lay the fields out in two columns — for a caller with the width to spare. */
  twoColumn?: boolean;
}) => {
  const countrySelectStyles = useMuiSelectStyles<(typeof countries)[number]>();

  return (
    <Stack sx={{ gap: '14px' }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: '8px' }}>
        <Stack sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
            Billing details
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, color: 'text.secondary', mt: '2px' }}
          >
            We need this once to complete your payment.
          </Typography>
        </Stack>
        <IconButton sx={{ p: '4px', flexShrink: 0 }} onClick={onClose}>
          <CloseRounded sx={{ fontSize: '22px', color: 'text.primary' }} />
        </IconButton>
      </Stack>

      <FormFields twoColumn={twoColumn}>
        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Name is required',
            validate: (value) =>
              value.trim().length >= 5 ||
              'Name must be at least 5 characters long',
          }}
          render={({ field, fieldState }) => (
            <Stack>
              <CheckoutLabel>Name</CheckoutLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="Enter your full name"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                slotProps={{ inputLabel: { shrink: false } }}
              />
            </Stack>
          )}
        />

        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email is required',
            validate: (value) =>
              EMAIL_REGEX.test(value) || 'Invalid email address',
          }}
          render={({ field, fieldState }) => (
            <Stack>
              <CheckoutLabel>Email Address</CheckoutLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="Enter your email"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                slotProps={{ inputLabel: { shrink: false } }}
              />
            </Stack>
          )}
        />

        <Controller
          name="phone.number"
          control={control}
          rules={{
            required: 'Phone number is required',
            validate: (value) =>
              PHONE_REGEX.test(value) || 'Invalid phone number',
          }}
          render={({ field, fieldState }) => (
            <Stack>
              <CheckoutLabel>Phone Number</CheckoutLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                type="number"
                placeholder="Enter your phone number"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                slotProps={{
                  inputLabel: { shrink: false },
                  htmlInput: {
                    onWheel: (e: WheelEvent<HTMLInputElement>) =>
                      e.currentTarget.blur(),
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: '4px' }}>
                        <Controller
                          name="phone.country_code"
                          control={control}
                          render={({ field: countryField }) => (
                            <MuiReactSelect
                              value={
                                countries.find(
                                  (c) =>
                                    c.value ===
                                    `+${countryField.value || '91'}`,
                                ) ?? null
                              }
                              onChange={(selected: any) =>
                                countryField.onChange(
                                  selected?.value?.slice(1) || '91',
                                )
                              }
                              options={countries}
                              getOptionLabel={(option: any) => option.value}
                              menuPosition="fixed"
                              styles={{
                                ...countrySelectStyles,
                                container: (base) => ({
                                  ...base,
                                  width: '76px',
                                }),
                                control: (base) => ({
                                  ...base,
                                  minHeight: 'unset',
                                  border: 'none',
                                  boxShadow: 'none',
                                  backgroundColor: 'transparent',
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  padding: 0,
                                }),
                                indicatorSeparator: (base) => ({
                                  ...base,
                                  display: 'none',
                                }),
                              }}
                            />
                          )}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>
          )}
        />

        <Controller
          name="billingAddress.country"
          control={control}
          rules={{ required: 'Country is required' }}
          render={({ field, fieldState }) => (
            <Stack>
              <CheckoutLabel>Country</CheckoutLabel>
              <Select
                {...field}
                value={field.value || 'India'}
                onChange={(event) => field.onChange(event.target.value)}
                fullWidth
                size="small"
                error={fieldState.invalid}
                IconComponent={ArrowDropDownRounded}
                MenuProps={{ slotProps: { paper: { sx: { zIndex: 1500 } } } }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.name} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                  {fieldState.error.message}
                </Typography>
              )}
            </Stack>
          )}
        />

        <FullRow>
          <Controller
            name="billingAddress.address"
            control={control}
            rules={{
              required: 'Address is required',
              validate: (value) =>
                value.trim().length >= 5 ||
                'Address must be at least 5 characters long',
            }}
            render={({ field, fieldState }) => (
              <Stack>
                <CheckoutLabel>Address</CheckoutLabel>
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Enter your full address"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  slotProps={{ inputLabel: { shrink: false } }}
                />
              </Stack>
            )}
          />
        </FullRow>

        <FullRow>
          <Stack direction="row" sx={{ gap: '12px' }}>
            <Controller
              name="billingAddress.pincode"
              control={control}
              rules={{
                required: 'Pincode is required',
                validate: (value) =>
                  PINCODE_REGEX.test(value) || 'Invalid pincode',
              }}
              render={({ field, fieldState }) => (
                <Stack sx={{ width: '50%' }}>
                  <CheckoutLabel>Pincode</CheckoutLabel>
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder="Pincode"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    slotProps={{ inputLabel: { shrink: false } }}
                  />
                </Stack>
              )}
            />
            <Controller
              name="billingAddress.state"
              control={control}
              rules={{
                required: 'State is required',
                validate: (value) =>
                  value.trim().length >= 3 || 'Please fill correct state',
              }}
              render={({ field, fieldState }) => (
                <Stack sx={{ width: '50%' }}>
                  <CheckoutLabel>State</CheckoutLabel>
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder="State"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    slotProps={{ inputLabel: { shrink: false } }}
                  />
                </Stack>
              )}
            />
          </Stack>
        </FullRow>
      </FormFields>

      <Stack
        component="button"
        type="button"
        onClick={onSubmit}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
          border: 'none',
          cursor: 'pointer',
          bgcolor: 'primary.main',
          color: 'common.white',
          borderRadius: '12px',
          py: '12px',
          fontSize: 14.5,
          fontWeight: 800,
          mt: '4px',
        }}
      >
        Continue
      </Stack>
    </Stack>
  );
};

export default BillingAddressForm;
